import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { ArrowRight, CheckCircle2, Loader2 } from 'lucide-react';
import { COALITION_EMAIL, CoalitionSignup, submitCoalitionSignup } from '../utils/coalitionSignup';
const EMPTY_FORM: CoalitionSignup = {
  name: '',
  email: '',
  city: '',
  sector: ''
};
export function JoinCoalition() {
  const [form, setForm] = useState<CoalitionSignup>(EMPTY_FORM);
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const updateField = (field: keyof CoalitionSignup, value: string) => {
    setForm(previous => ({
      ...previous,
      [field]: value
    }));
    setError('');
  };
  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!form.name.trim()) {
      setError('Please enter your name.');
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) {
      setError('Please enter a valid email address.');
      return;
    }
    setIsSubmitting(true);
    try {
      await submitCoalitionSignup(form);
      setSubmitted(true);
    } catch {
      setError(`We could not save your information right now. Please email ${COALITION_EMAIL} and we will add you.`);
    } finally {
      setIsSubmitting(false);
    }
  };
  return <section id="join" className="py-24 bg-white scroll-mt-24" data-id="element-155" data-mp="2fn0n9swic5oy">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-156" data-mp="33btljw0lqpmq">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.5
      }} className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center" data-id="element-157" data-mp="389btpk6sp0to">
          <div data-id="element-158" data-mp="2yhpg5h6zipnr">
            <p className="text-pac-red font-bold uppercase tracking-[0.2em] text-sm mb-3" data-id="element-159" data-mp="3m9cy39m9qqdw">
              Add Your Voice
            </p>
            <h2 className="font-serif text-4xl md:text-5xl font-bold text-pac-navy mb-5" data-id="element-160" data-mp="3r14p1l9c6y4k">
              Join the Coalition
            </h2>
            <p className="text-lg text-slate-600 leading-relaxed mb-6" data-id="element-161" data-mp="4ol4ljpocjkh">
              Arizona is not one community. It is hundreds. Tell us where you
              call home and we&apos;ll keep you posted on the work to protect
              local decision-making across the state.
            </p>
            <p className="text-sm text-slate-500 leading-relaxed" data-id="element-162" data-mp="3mydary2w7mcl">
              We&apos;ll only email about coalition updates, events, and ways to
              get involved. Your information is never sold.
            </p>
          </div>

          <div className="bg-pac-sand p-8 rounded-2xl border border-slate-200 shadow-sm" data-id="element-163" data-mp="386uz2ch2gdz1">
            {submitted ? <div className="text-center py-6" role="status" data-id="element-164" data-mp="339cko6ka56jn">
                <div className="inline-flex items-center justify-center w-14 h-14 bg-pac-navy rounded-full mb-5" data-id="element-165" data-mp="2z6ga934w8gqs">
                  <CheckCircle2 className="h-7 w-7 text-pac-gold" data-id="element-166" data-mp="2u73efsw1nzc2" />
                </div>
                <h3 className="font-serif text-2xl font-bold text-pac-navy mb-2" data-id="element-167" data-mp="19pufovdgg84r">
                  Welcome to the coalition.
                </h3>
                <p className="text-slate-600 mb-6" data-id="element-168" data-mp="1en454b0ugjsl">
                  Thanks, {form.name.split(' ')[0]}. We&apos;ll be in touch at{' '}
                  <span className="font-semibold text-pac-navy" data-id="element-169" data-mp="1npdcis0tfx1r">
                    {form.email}
                  </span>
                  .
                </p>
                <button type="button" onClick={() => {
              setForm(EMPTY_FORM);
              setSubmitted(false);
            }} className="text-pac-blue font-bold hover:text-pac-navy transition-colors focus:outline-none focus:ring-2 focus:ring-pac-gold focus:ring-offset-2 rounded" data-id="element-170" data-mp="r0nxd9gkpijh">
                  Add another supporter
                </button>
              </div> : <form onSubmit={handleSubmit} noValidate className="space-y-5" data-id="element-171" data-mp="c8vrrp6kziuy">
                <div data-id="element-172" data-mp="2ew2fwipz2kjs">
                  <label htmlFor="join-name" className="block text-sm font-bold text-pac-navy uppercase tracking-wider mb-2" data-id="element-173" data-mp="2exncedvukdka">
                    Name
                  </label>
                  <input id="join-name" type="text" autoComplete="name" value={form.name} onChange={event => updateField('name', event.target.value)} className="w-full px-4 py-3 bg-white border-2 border-slate-200 rounded font-medium text-slate-800 outline-none transition-colors focus:border-pac-blue" placeholder="Your full name" data-id="element-174" data-mp="2nr0cbzsbwffv" />
                </div>

                <div data-id="element-175" data-mp="1ca0n5d1a2gq2">
                  <label htmlFor="join-email" className="block text-sm font-bold text-pac-navy uppercase tracking-wider mb-2" data-id="element-176" data-mp="1c98otugaq4w3">
                    Email
                  </label>
                  <input id="join-email" type="email" autoComplete="email" value={form.email} onChange={event => updateField('email', event.target.value)} className="w-full px-4 py-3 bg-white border-2 border-slate-200 rounded font-medium text-slate-800 outline-none transition-colors focus:border-pac-blue" placeholder="you@example.com" data-id="element-177" data-mp="y5a1p83x4b7x" />
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5" data-id="element-178" data-mp="5gky92fe3tt0">
                  <div data-id="element-179" data-mp="aew3ezuqpg4w">
                    <label htmlFor="join-city" className="block text-sm font-bold text-pac-navy uppercase tracking-wider mb-2" data-id="element-180" data-mp="ae4c4186e4kf">
                      City <span className="font-normal normal-case text-slate-400" data-id="element-181" data-mp="24t6y3oy0n4il">(optional)</span>
                    </label>
                    <input id="join-city" type="text" value={form.city} onChange={event => updateField('city', event.target.value)} className="w-full px-4 py-3 bg-white border-2 border-slate-200 rounded font-medium text-slate-800 outline-none transition-colors focus:border-pac-blue" placeholder="Goodyear" data-id="element-182" data-mp="22f1szo45sohz" />
                  </div>

                  <div data-id="element-183" data-mp="2h5iraylhyyan">
                    <label htmlFor="join-sector" className="block text-sm font-bold text-pac-navy uppercase tracking-wider mb-2" data-id="element-184" data-mp="2cbwmttphm0px">
                      Sector <span className="font-normal normal-case text-slate-400" data-id="element-185" data-mp="34t91f1jnltw7">(optional)</span>
                    </label>
                    <select id="join-sector" value={form.sector} onChange={event => updateField('sector', event.target.value)} className="w-full px-4 py-3 bg-white border-2 border-slate-200 rounded font-medium text-slate-800 outline-none transition-colors focus:border-pac-blue" data-id="element-186" data-mp="300zb3kemmrte">
                      <option value="" data-id="element-187" data-mp="3m8czo7mz8jwh">Select one</option>
                      <option value="public" data-id="element-188" data-mp="3qazvskqokqzh">Public</option>
                      <option value="private" data-id="element-189" data-mp="3vb48dzogx1kf">Private</option>
                      <option value="nonprofit" data-id="element-190" data-mp="3v9b3aoti6pej">Nonprofit</option>
                      <option value="resident" data-id="element-191" data-mp="3w6qmy8u70m9">Resident</option>
                    </select>
                  </div>
                </div>

                {error && <p className="text-sm font-medium text-pac-red" role="alert" data-id="element-192" data-mp="dumi52bs0bwa">
                    {error}
                  </p>}

                <button type="submit" disabled={isSubmitting} className="w-full bg-pac-navy hover:bg-pac-blue disabled:bg-slate-400 disabled:cursor-not-allowed text-white py-4 rounded font-bold uppercase tracking-wider transition-colors flex items-center justify-center gap-2 group focus:outline-none focus:ring-2 focus:ring-pac-gold focus:ring-offset-2" data-id="element-193" data-mp="hx10llbj99n1">
                  {isSubmitting ? <>
                      Joining
                      <Loader2 className="h-5 w-5 animate-spin" data-id="element-194" data-mp="kgy8rw7mog3d" />
                    </> : <>
                      Join the Coalition
                      <ArrowRight className="h-5 w-5 group-hover:translate-x-1 transition-transform" data-id="element-195" data-mp="u79osh4l6fqm" />
                    </>}
                </button>
              </form>}
          </div>
        </motion.div>
      </div>
    </section>;
}